import { AddressInput } from "@/components/molecules/address-input";
import { PhoneInput } from "@/components/molecules/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import type { GeocodingResult } from "@/features/maps/types";
import type {
  Address,
  Customer,
  EmailAddress,
  PhoneNumber,
} from "@/features/customers/types";
import { PlusIcon, TrashIcon } from "@phosphor-icons/react";
import { useMemo } from "react";
import { EmailItem, PhoneItem } from "./contact-item";
import { useTranslation } from "@/features/utils/i18n";

interface CustomerFormProps {
  customer?: Customer;
  onChange: (data: Partial<Customer>) => void;
  mode?: "create" | "edit";
  showNotes?: boolean;
}

export function CustomerForm({
  customer,
  onChange,
  mode = "edit",
  showNotes = false,
}: CustomerFormProps) {
  const { t } = useTranslation("customers");

  // Determine which accordion items should be open by default
  const defaultOpenAccordions = useMemo(() => {
    const open: string[] = [];

    // Organization accordion
    if (
      customer?.organization?.company ||
      customer?.organization?.department ||
      customer?.organization?.title
    ) {
      open.push("organization");
    }

    // Personal details accordion (gender/birthday)
    if (customer?.gender || customer?.birthday) {
      open.push("personal-details");
    }

    // Relations accordion
    if (
      customer?.relations?.contacts &&
      customer.relations.contacts.length > 0
    ) {
      open.push("relations");
    }

    return open;
  }, [customer]);

  const isCreateMode = mode === "create";
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const updateField = (field: keyof Customer, value: any) => {
    onChange({ [field]: value });
  };

  const updateNestedField = (
    parent: keyof Customer,
    field: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    value: any,
  ) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parentObj = (customer?.[parent] || {}) as any;
    onChange({
      [parent]: {
        ...parentObj,
        [field]: value,
      },
    });
  };

  // Email handlers
  const addEmail = () => {
    const emails = customer?.addresses?.email || [];
    updateNestedField("addresses", "email", [
      ...emails,
      { address: "", type: "other" as const, primary: emails.length === 0 },
    ]);
  };

  const removeEmail = (index: number) => {
    const emails = customer?.addresses?.email || [];
    const newEmails = emails.filter((_, i) => i !== index);
    // If we removed the primary email, make the first one primary
    if (emails[index]?.primary && newEmails.length > 0) {
      newEmails[0].primary = true;
    }
    updateNestedField("addresses", "email", newEmails);
  };

  const updateEmail = (index: number, updates: Partial<EmailAddress>) => {
    const emails = [...(customer?.addresses?.email || [])];
    // If setting a new primary, clear others
    if (updates.primary) {
      emails.forEach((e, i) => {
        if (i !== index) e.primary = false;
      });
    }
    emails[index] = { ...emails[index], ...updates };
    updateNestedField("addresses", "email", emails);
  };

  // Phone handlers
  const addPhone = () => {
    const phones = customer?.addresses?.phone || [];
    updateNestedField("addresses", "phone", [
      ...phones,
      { number: "", type: "mobile" as const, primary: phones.length === 0 },
    ]);
  };

  const removePhone = (index: number) => {
    const phones = customer?.addresses?.phone || [];
    const newPhones = phones.filter((_, i) => i !== index);
    // If we removed the primary phone, make the first one primary
    if (phones[index]?.primary && newPhones.length > 0) {
      newPhones[0].primary = true;
    }
    updateNestedField("addresses", "phone", newPhones);
  };

  const updatePhone = (index: number, updates: Partial<PhoneNumber>) => {
    const phones = [...(customer?.addresses?.phone || [])];
    // If setting a new primary, clear others
    if (updates.primary) {
      phones.forEach((p, i) => {
        if (i !== index) p.primary = false;
      });
    }
    phones[index] = { ...phones[index], ...updates };
    updateNestedField("addresses", "phone", phones);
  };

  // Address handlers
  const addAddress = () => {
    const addresses = customer?.addresses?.postal || [];
    updateNestedField("addresses", "postal", [
      ...addresses,
      { full: "", type: "home" as const, primary: addresses.length === 0 },
    ]);
  };

  const removeAddress = (index: number) => {
    const addresses = customer?.addresses?.postal || [];
    const newAddresses = addresses.filter((_, i) => i !== index);
    // If we removed the primary address, make the first one primary
    if (addresses[index]?.primary && newAddresses.length > 0) {
      newAddresses[0].primary = true;
    }
    updateNestedField("addresses", "postal", newAddresses);
  };

  const updateAddress = (index: number, updates: Partial<Address>) => {
    const addresses = [...(customer?.addresses?.postal || [])];
    // If setting a new primary, clear others
    if (updates.primary) {
      addresses.forEach((a, i) => {
        if (i !== index) a.primary = false;
      });
    }
    addresses[index] = { ...addresses[index], ...updates };
    updateNestedField("addresses", "postal", addresses);
  };

  const handleAddressSelect = (index: number, result: GeocodingResult) => {
    updateAddress(index, {
      full: result.formatted,
      street: result.street,
      city: result.city,
      region: result.state,
      postal_code: result.postcode,
      country: result.country,
    });
  };

  const emails = customer?.addresses?.email || [];
  const phones = customer?.addresses?.phone || [];
  const postalAddresses = customer?.addresses?.postal || [];

  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <div className="grid gap-4 md:grid-cols-[100px_1fr_1fr]">
        <div className="space-y-2">
          <Label htmlFor="prefix">{t("form.prefix")}</Label>
          <Select
            value={customer?.prefix || ""}
            onValueChange={(value) => updateField("prefix", value)}
          >
            <SelectTrigger id="prefix">
              <SelectValue placeholder={t("form.prefixOptions.none")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">{t("form.prefixOptions.none")}</SelectItem>
              <SelectItem value="Mr.">{t("form.prefixOptions.mr")}</SelectItem>
              <SelectItem value="Mrs.">
                {t("form.prefixOptions.mrs")}
              </SelectItem>
              <SelectItem value="Ms.">{t("form.prefixOptions.ms")}</SelectItem>
              <SelectItem value="Dr.">{t("form.prefixOptions.dr")}</SelectItem>
              <SelectItem value="Prof.">
                {t("form.prefixOptions.prof")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="first_name">{t("form.givenName")}</Label>
          <Input
            id="first_name"
            value={customer?.given_name || ""}
            onChange={(e) => updateField("given_name", e.target.value)}
            placeholder={t("form.placeholders.firstName")}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name">{t("form.familyName")}</Label>
          <Input
            id="last_name"
            value={customer?.family_name || ""}
            onChange={(e) => updateField("family_name", e.target.value)}
            placeholder={t("form.placeholders.lastName")}
          />
        </div>
      </div>

      {/* Gender and Birthday - only in edit mode or as accordion in create */}
      {!isCreateMode && (
        <Accordion
          type="multiple"
          defaultValue={defaultOpenAccordions}
          className="w-full"
        >
          <AccordionItem value="personal-details">
            <AccordionTrigger className="text-sm font-medium">
              {t("form.additionalPersonalDetails")}
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="gender">{t("form.gender")}</Label>
                  <Select
                    value={customer?.gender || ""}
                    onValueChange={(value) => updateField("gender", value)}
                  >
                    <SelectTrigger id="gender">
                      <SelectValue placeholder={t("form.selectGender")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">
                        {t("form.genderOptions.notSpecified")}
                      </SelectItem>
                      <SelectItem value="M">
                        {t("form.genderOptions.male")}
                      </SelectItem>
                      <SelectItem value="F">
                        {t("form.genderOptions.female")}
                      </SelectItem>
                      <SelectItem value="O">
                        {t("form.genderOptions.other")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="birthday">{t("form.birthday")}</Label>
                  <Input
                    id="birthday"
                    type="date"
                    value={customer?.birthday || ""}
                    onChange={(e) => updateField("birthday", e.target.value)}
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* Email Addresses */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t("form.emailAddresses")}</h3>
          <Button type="button" variant="outline" size="sm" onClick={addEmail}>
            <PlusIcon className="mr-2 h-4 w-4" />
            {t("actions.addEmail")}
          </Button>
        </div>
        {emails.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t("form.noEmailsAdded")}
          </p>
        )}
        {emails.map((email, index) => (
          <EmailItem
            key={index}
            email={email}
            onUpdate={(updates) => updateEmail(index, updates)}
            onRemove={() => removeEmail(index)}
            canRemove={emails.length > 1}
          />
        ))}
      </div>

      {/* Phone Numbers */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t("form.phoneNumbers")}</h3>
          <Button type="button" variant="outline" size="sm" onClick={addPhone}>
            <PlusIcon className="mr-2 h-4 w-4" />
            {t("actions.addPhone")}
          </Button>
        </div>
        {phones.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t("form.noPhonesAdded")}
          </p>
        )}
        {phones.map((phone, index) => (
          <PhoneItem
            key={index}
            phone={phone}
            onUpdate={(updates) => updatePhone(index, updates)}
            onRemove={() => removePhone(index)}
            canRemove={phones.length > 1}
            PhoneInputComponent={PhoneInput}
          />
        ))}
      </div>

      {/* Call handling */}
      <div className="space-y-2">
        <Label htmlFor="call_handling">{t("form.callHandling.label")}</Label>
        <Select
          value={customer?.call_handling || "normal"}
          onValueChange={(value) =>
            updateField("call_handling", value === "normal" ? "" : value)
          }
        >
          <SelectTrigger id="call_handling" className="md:w-80">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="normal">
              {t("form.callHandling.options.normal")}
            </SelectItem>
            <SelectItem value="vip">
              {t("form.callHandling.options.vip")}
            </SelectItem>
            <SelectItem value="blocked">
              {t("form.callHandling.options.blocked")}
            </SelectItem>
          </SelectContent>
        </Select>
        <p className="text-sm text-muted-foreground">
          {t(
            `form.callHandling.descriptions.${customer?.call_handling || "normal"}`,
          )}
        </p>
      </div>

      {/* Postal Addresses */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t("form.postalAddresses")}</h3>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addAddress}
          >
            <PlusIcon className="mr-2 h-4 w-4" />
            {t("actions.addAddress")}
          </Button>
        </div>
        {postalAddresses.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {t("form.noAddressesAdded")}
          </p>
        )}
        {postalAddresses.map((address, index) => (
          <div key={index} className="space-y-2 rounded-lg border p-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <AddressInput
                  value={address.full}
                  onChange={(value) => updateAddress(index, { full: value })}
                  onSelect={(result) => handleAddressSelect(index, result)}
                  placeholder={t("form.placeholders.address")}
                />
              </div>
              <div className="w-32">
                <Select
                  value={address.type || "home"}
                  onValueChange={(value) =>
                    updateAddress(index, {
                      type: value as Address["type"],
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="home">
                      {t("contactTypes.address.home")}
                    </SelectItem>
                    <SelectItem value="work">
                      {t("contactTypes.address.work")}
                    </SelectItem>
                    <SelectItem value="other">
                      {t("contactTypes.address.other")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant={address.primary ? "default" : "outline"}
                  size="sm"
                  onClick={() =>
                    updateAddress(index, { primary: !address.primary })
                  }
                  title={
                    address.primary
                      ? t("common.primaryAddress")
                      : t("form.setPrimaryAddress")
                  }
                >
                  {address.primary ? t("common.primary") : t("form.setPrimary")}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeAddress(index)}
                  disabled={postalAddresses.length === 1}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
            {/* Show structured address details if available */}
            {(address.street || address.city || address.region) && (
              <div className="grid gap-2 text-sm text-muted-foreground">
                {address.street && (
                  <div>
                    {t("form.street")}: {address.street}
                  </div>
                )}
                {address.city && (
                  <div>
                    {t("form.city")}: {address.city}
                  </div>
                )}
                {address.region && (
                  <div>
                    {t("form.stateRegion")}: {address.region}
                  </div>
                )}
                {address.postal_code && (
                  <div>
                    {t("form.postalCode")}: {address.postal_code}
                  </div>
                )}
                {address.country && (
                  <div>
                    {t("form.country")}: {address.country}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Organization - as Accordion */}
      <Accordion
        type="multiple"
        defaultValue={defaultOpenAccordions}
        className="w-full"
      >
        <AccordionItem value="organization">
          <AccordionTrigger className="text-lg font-semibold">
            {t("form.organization")}
          </AccordionTrigger>
          <AccordionContent className="space-y-4 pt-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="company">{t("form.company")}</Label>
                <Input
                  id="company"
                  value={customer?.organization?.company || ""}
                  onChange={(e) =>
                    updateNestedField("organization", "company", e.target.value)
                  }
                  placeholder={t("form.placeholders.company")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="department">{t("form.department")}</Label>
                <Input
                  id="department"
                  value={customer?.organization?.department || ""}
                  onChange={(e) =>
                    updateNestedField(
                      "organization",
                      "department",
                      e.target.value,
                    )
                  }
                  placeholder={t("form.placeholders.department")}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">{t("form.title")}</Label>
              <Input
                id="title"
                value={customer?.organization?.title || ""}
                onChange={(e) =>
                  updateNestedField("organization", "title", e.target.value)
                }
                placeholder={t("form.placeholders.title")}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {/* Related Contacts - only in edit mode */}
      {!isCreateMode && (
        <Accordion
          type="multiple"
          defaultValue={defaultOpenAccordions}
          className="w-full"
        >
          <AccordionItem value="relations">
            <AccordionTrigger className="text-lg font-semibold">
              {t("form.relations")}
            </AccordionTrigger>
            <AccordionContent className="space-y-4 pt-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted-foreground">
                  {t("form.relationsDescription")}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const contacts = customer?.relations?.contacts || [];
                    updateField("relations", {
                      contacts: [
                        ...contacts,
                        {
                          given_name: "",
                          family_name: "",
                          addresses: { email: [], phone: [], postal: [] },
                        },
                      ],
                    });
                  }}
                >
                  <PlusIcon className="mr-2 h-4 w-4" />
                  {t("form.addRelatedContact")}
                </Button>
              </div>
              {(!customer?.relations?.contacts ||
                customer.relations.contacts.length === 0) && (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t("form.noRelatedContacts")}
                </p>
              )}
              {customer?.relations?.contacts?.map((contact, index) => (
                <div key={index} className="rounded-lg border p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">
                      {t("form.contact")} {index + 1}
                    </h4>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const contacts = customer?.relations?.contacts || [];
                        updateField("relations", {
                          contacts: contacts.filter((_, i) => i !== index),
                        });
                      }}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label>{t("form.givenName")}</Label>
                      <Input
                        value={contact.given_name || ""}
                        onChange={(e) => {
                          const contacts = [
                            ...(customer?.relations?.contacts || []),
                          ];
                          contacts[index] = {
                            ...contacts[index],
                            given_name: e.target.value,
                          };
                          updateField("relations", { contacts });
                        }}
                        placeholder={t("form.placeholders.firstName")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("form.familyName")}</Label>
                      <Input
                        value={contact.family_name || ""}
                        onChange={(e) => {
                          const contacts = [
                            ...(customer?.relations?.contacts || []),
                          ];
                          contacts[index] = {
                            ...contacts[index],
                            family_name: e.target.value,
                          };
                          updateField("relations", { contacts });
                        }}
                        placeholder={t("form.placeholders.lastName")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("form.email")}</Label>
                      <Input
                        type="email"
                        value={contact.addresses?.email?.[0]?.address || ""}
                        onChange={(e) => {
                          const contacts = [
                            ...(customer?.relations?.contacts || []),
                          ];
                          contacts[index] = {
                            ...contacts[index],
                            addresses: {
                              ...(contacts[index].addresses || {
                                email: [],
                                phone: [],
                                postal: [],
                              }),
                              email: [
                                { address: e.target.value, primary: true },
                              ],
                            },
                          };
                          updateField("relations", { contacts });
                        }}
                        placeholder={t("form.placeholders.email")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t("form.phone")}</Label>
                      <PhoneInput
                        value={contact.addresses?.phone?.[0]?.number || ""}
                        onChange={(number) => {
                          const contacts = [
                            ...(customer?.relations?.contacts || []),
                          ];
                          contacts[index] = {
                            ...contacts[index],
                            addresses: {
                              ...(contacts[index].addresses || {
                                email: [],
                                phone: [],
                                postal: [],
                              }),
                              phone: [{ number: number || "", primary: true }],
                            },
                          };
                          updateField("relations", { contacts });
                        }}
                        placeholder={t("form.placeholders.phone")}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label>{t("form.company")}</Label>
                      <Input
                        value={contact.organization?.company || ""}
                        onChange={(e) => {
                          const contacts = [
                            ...(customer?.relations?.contacts || []),
                          ];
                          contacts[index] = {
                            ...contacts[index],
                            organization: {
                              ...(contacts[index].organization || {}),
                              company: e.target.value,
                            },
                          };
                          updateField("relations", { contacts });
                        }}
                        placeholder={t("form.placeholders.company")}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      )}

      {/* Notes */}
      {showNotes && (
        <div className="space-y-2">
          <Label htmlFor="notes">{t("form.notes")}</Label>
          <Textarea
            id="notes"
            value={customer?.notes || ""}
            onChange={(e) => updateField("notes", e.target.value)}
            placeholder={t("form.placeholders.notes")}
            rows={4}
          />
        </div>
      )}
    </div>
  );
}
